import { SublocationWithDevices } from '@leid/api-locations'
import { Order, OrderItem, PickupStatus } from '@leid/api-orders'
import { ImageSequenceStep } from '@leid/api-products'

import apiClient from 'helpers/clients/apiClient'
import { getDifferenceInHours, utcNowMoment } from 'utils/dates'

import { getDoorsWithDirectionForSublocation } from '../../../utils/devices'
import {
  AliasInfo,
  CommitStepData,
  DeviceDirection,
  FlowDirection,
  FlowStepDescription,
  ManyAssetsReminderStepData,
  OpenDoorPINStepData,
  OpenDoorStepData,
  PickUpAssetsStepData,
  ReturnAssetsStepData,
  StepType,
  TakePhotosStepData,
} from '../models'
import { Device } from '@leid/api-assets'

export const getItemsForFlow = async (
  direction: FlowDirection,
  orderItem: OrderItem,
  orderItems: OrderItem[],
  order: Order,
  isPublicPage: boolean,
): Promise<OrderItem[] | undefined> => {
  // First filter items based on current data
  const tempList = getTempListOfItemsToProcess(direction, orderItem, orderItems, order)
  // Filter filtered items based on new data from DB
  const finalList = await checkItemsRealStateBeforeProcessing(direction, tempList, orderItem, order, isPublicPage)
  return finalList
}

export const getOrderItemFlowSteps = async (
  orderItem: OrderItem,
  devices: SublocationWithDevices[],
  direction: FlowDirection,
): Promise<FlowStepDescription[] | undefined> => {
  const assets = await getAssets(orderItem.locationId, orderItem.productId)
  if (!assets) {
    // No assets - nothing to do
    return undefined
  }

  const sublocationId = assets[0].sublocationId
  const sublocationsChain = buildSublocationsChain(sublocationId, devices)

  const imageSequenceSteps = await getImageSequenceSteps(orderItem.productId ?? 0)
  if (!imageSequenceSteps) {
    // No image steps - nothing to do
    return undefined
  }

  const flowSteps: FlowStepDescription[] = []

  // Input doors
  for (const sublocation of sublocationsChain) {
    const inDoors = getDoorsWithDirectionForSublocation(DeviceDirection.IN, sublocation)

    for (const doors of inDoors) {
      const outputs = doors.outputs.map<OpenDoorStepData>((output) => ({
        sublocationId: doors.sublocationId,
        deviceId: output.id,
        doorName: output.name,

        orderId: orderItem.orderId,
        customerId: orderItem.customerId,
        locationId: orderItem.locationId,
      }))

      flowSteps.push(
        ...outputs.map((s) => ({
          type: StepType.OPEN_DOOR,
          data: s,
        })),
      )
    }

    // Add PIN devices
    if (sublocation.assetDeviceId) {
      const sublocationDevices = await getSublocationDevices(sublocation.assetDeviceId)
      if (!sublocationDevices) {
        // Error happened
        return undefined
      }

      const pinDoors = sublocationDevices.map<OpenDoorPINStepData>((output) => ({
        sublocationId: sublocation.sublocationId,
        deviceId: output.id,
        doorName: output.name,

        orderId: orderItem.orderId,
        customerId: orderItem.customerId,
        locationId: orderItem.locationId,
      }))
      flowSteps.push(
        ...pinDoors.map((s) => ({
          type: StepType.OPEN_DOOR_PIN,
          data: s,
        })),
      )
    }
  }

  // Add select asset step
  if (orderItem.amount > 1) {
    // If many assets - only confirmation
    const stepData: ManyAssetsReminderStepData = {
      flowDirection: direction,
      amount: orderItem.amount,
      productName: orderItem.productData?.name ?? '',

      orderItemId: orderItem.id,
      orderId: orderItem.orderId,
      locationId: orderItem.locationId,
    }

    flowSteps.push({
      type: StepType.MANY_ASSETS_REMINDER,
      data: stepData,
    })
  } else {
    if (direction === FlowDirection.PICKUP) {
      const stepData: PickUpAssetsStepData = {
        assets: assets,

        orderItemId: orderItem.id,
        orderId: orderItem.orderId,
        locationId: orderItem.locationId,
      }

      flowSteps.push({
        type: StepType.PICKUP_ASSET,
        data: stepData,
      })
    }

    if (direction === FlowDirection.RETURN) {
      const stepData: ReturnAssetsStepData = {
        orderItemId: orderItem.id,
        asset: assets.find((s) => s.id === orderItem.assetId),
      }

      flowSteps.push({
        type: StepType.RETURN_ASSET,
        data: stepData,
      })
    }
  }

  // Take photo step
  const takePhotoStepData: TakePhotosStepData = {
    orderId: orderItem.orderId,
    orderItemId: orderItem.id,
    locationId: orderItem.locationId,
    imageSequenceSteps: imageSequenceSteps,
  }
  flowSteps.push({
    type: StepType.TAKE_PHOTOS,
    data: takePhotoStepData,
  })

  // Commit step
  const commitStepData: CommitStepData = {
    orderId: orderItem.orderId,
    orderItemId: orderItem.id,
    customerId: orderItem.customerId,
    productId: orderItem.productId,
    flowDirection: direction,
  }
  flowSteps.push({
    type: StepType.COMMIT,
    data: commitStepData,
  })

  return flowSteps
}

const getAssets = async (
  locationId: number | undefined,
  productId: number | undefined,
): Promise<AliasInfo[] | undefined> => {
  const response = await apiClient.getAssets({
    active: true,
    defaultLocationIds: [locationId ?? 0],
    product_id: productId,
  })
  if (!response.success || !response.data) {
    return undefined
  }

  const assets = response.data.rows
    .filter((s) => s.visual_tag !== undefined)
    .map<AliasInfo>((s) => ({
      id: s.id,
      visualTag: s.visual_tag ?? '',
      available: s.order_item_id === undefined,
      sublocationId: s.default_sub_location_id,
    }))

  if (assets.length === 0) {
    return undefined
  }

  return assets
}

const getImageSequenceSteps = async (productId: number): Promise<ImageSequenceStep[] | undefined> => {
  const productResponse = await apiClient.getProduct(productId)
  if (!productResponse.success || !productResponse.data) {
    return undefined
  }

  const product = productResponse.data
  if (!product.imageSequenceId) {
    // No steps
    return []
  }

  const imageSequenceResponse = await apiClient.getImageSequence(product.imageSequenceId)
  if (!imageSequenceResponse.success || !imageSequenceResponse.data) {
    return undefined
  }

  const imageSequence = imageSequenceResponse.data

  if (!imageSequence || !imageSequence.active) {
    // No steps
    return []
  }

  const response = await apiClient.getImageSequenceSteps({
    active: true,
    sequenceId: imageSequence.id,
  })
  if (!response.success || !response.data) {
    return undefined
  }

  return response.data.rows
}

const buildSublocationsChain = (
  sublocationId: number,
  devices: SublocationWithDevices[],
  result: SublocationWithDevices[] = [],
): SublocationWithDevices[] => {
  const device = devices.find((s) => s.sublocationId === sublocationId)
  if (!device) {
    return []
  }

  if (device.parentSublocationId) {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const updatedResult = buildSublocationsChain(device.parentSublocationId, devices, result)
    updatedResult.push(device)
    return updatedResult
  }

  result.push(device)
  return result
}

const getSublocationDevices = async (assetDeviceId: number): Promise<Device[] | undefined> => {
  const response = await apiClient.getAssetDevices({
    ids: [assetDeviceId],
    active: true,
  })
  if (!response.success || !response.data) {
    console.log(`Error on getting sublocation devices`, assetDeviceId, response)
    return undefined
  }
  return response.data?.rows
}

export const isOrderItemSuitableForPickup = (
  checkItem: OrderItem,
  order: Order,
  locationId: number | undefined,
): boolean => {
  if (checkItem.productData?.complementary === true) {
    // Complementary
    return false
  }
  if (checkItem.pickupStatus !== PickupStatus.EMPTY) {
    // If picked up, but no asset Id set - continue flow
    if (checkItem.pickupStatus !== PickupStatus.PICKEDUP || checkItem.assetId) {
      // Already picked up
      return false
    }
  }
  if (checkItem.locationId !== locationId) {
    // From different location
    return false
  }
  if (order.paidDate && checkItem.rentedFrom) {
    const difference = getDifferenceInHours(order.paidDate, checkItem.rentedFrom)
    if (difference > 0 && difference <= 1) {
      // If order was paid less than an hour before order item start - it is available for pickup
      return true
    }
  }

  const utcNow = utcNowMoment().add(15, 'minutes')
  if (!checkItem.rentedFrom || checkItem.rentedFrom.getTime() > utcNow.toDate().getTime()) {
    // Can not be picked up now
    return false
  }

  return true
}

export const isOrderItemSuitableForReturn = (checkItem: OrderItem, locationId: number | undefined): boolean => {
  if (checkItem.productData?.complementary === true) {
    // Complementary
    return false
  }
  if (checkItem.pickupStatus !== PickupStatus.PICKEDUP) {
    // Not yet picked up
    return false
  }
  if (checkItem.locationId !== locationId) {
    // From different location
    return false
  }
  const utcNow = utcNowMoment().subtract(15, 'minutes')
  if (!checkItem.rentedTo || checkItem.rentedTo.getTime() < utcNow.toDate().getTime()) {
    // Can not be picked up now
    return false
  }
  return true
}

const getTempListOfItemsToProcess = (
  direction: FlowDirection,
  orderItem: OrderItem,
  orderItems: OrderItem[],
  order: Order,
): OrderItem[] => {
  if (direction === FlowDirection.PICKUP && orderItem.productData?.complementary === true) {
    return [orderItem]
  }

  const additionalItems: OrderItem[] = []

  switch (direction) {
    case FlowDirection.PICKUP:
      const itemsToPickup = orderItems.filter((s) => isOrderItemSuitableForPickup(s, order, orderItem.locationId))
      additionalItems.push(...itemsToPickup)
      break
    case FlowDirection.RETURN:
      const itemsToReturn = orderItems.filter((s) => isOrderItemSuitableForReturn(s, orderItem.locationId))
      additionalItems.push(...itemsToReturn)
      break
  }

  return [orderItem, ...additionalItems.filter((s) => s.id !== orderItem.id)]
}

const checkItemsRealStateBeforeProcessing = async (
  direction: FlowDirection,
  tempList: OrderItem[],
  orderItem: OrderItem,
  order: Order,
  isPublicPage: boolean,
): Promise<OrderItem[] | undefined> => {
  if (tempList.length === 1 && direction === FlowDirection.RETURN) {
    // We allow to run RETURN one more time. In case we wrongly detect return and customer want to change something
    const orderItemResponse = await (isPublicPage
      ? apiClient.getOrderItemWithoutCurrentCustomer(orderItem.id, order.orderCode ?? '')
      : apiClient.getOrderItem(orderItem.id))
    if (!orderItemResponse.success || !orderItemResponse.data) {
      return undefined
    }

    const { orderItem: refreshedOrderItem } = orderItemResponse.data
    if (refreshedOrderItem.pickupStatus === PickupStatus.RETURNED) {
      return [refreshedOrderItem]
    }
  }

  const finalList: OrderItem[] = []

  for (const tmpOrderItem of tempList) {
    if (direction === FlowDirection.PICKUP) {
      if (tmpOrderItem.productData && tmpOrderItem.productData.complementary) {
        const setStatusResponse = await apiClient.setOrderItemPickupStatus(tmpOrderItem.id, {
          orderId: tmpOrderItem.orderId,
          orderItemId: tmpOrderItem.id,
          pickupStatus: PickupStatus.COMPLETED,
        })
        if (!setStatusResponse.success || !setStatusResponse.data) {
          return undefined
        }

        // Do not add to final list
        continue
      }
    }

    const orderItemResponse = await (isPublicPage
      ? apiClient.getOrderItemWithoutCurrentCustomer(tmpOrderItem.id, order.orderCode ?? '')
      : apiClient.getOrderItem(tmpOrderItem.id))
    if (!orderItemResponse.success || !orderItemResponse.data) {
      return undefined
    }

    const { orderItem: refreshedOrderItem } = orderItemResponse.data

    switch (direction) {
      case FlowDirection.PICKUP:
        if (isOrderItemSuitableForPickup(refreshedOrderItem, order, orderItem.locationId)) {
          finalList.push(refreshedOrderItem)
        }
        break
      case FlowDirection.RETURN:
        if (isOrderItemSuitableForReturn(refreshedOrderItem, orderItem.locationId)) {
          finalList.push(refreshedOrderItem)
        }
        break
    }
  }

  return finalList
}
